import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table";
import t from "@/lang/lang";
import { ReactNode } from "react";
import {
  ListRequest,
  useHtzOrderListQuery,
} from "@/services/backend/htz/orders/api/order-api";
import { Order } from "@/services/backend/htz/orders/types/order";
import { DocumentReferenceNumberById } from "@/services/backend/documents/components/document-reference-number-by-id";
import { GSMServiceKind } from "@/services/backend/services/types/kinds";
import { GSMDocumentKind } from "@/services/backend/documents/types/kind";
import { formatDate } from "@/shared/lib/utilities/date";
import { isZeroUUID } from "@/shared/lib/utilities/uuid";
import { TableCard } from "@/shared/components/tables/table-card";
import { TableLoadingRow } from "@/shared/components/tables/table-loading-row";
import { TableRtkErrorRow } from "@/shared/components/tables/table-rtk-error-row";
import { TableEmptyRow } from "@/shared/components/tables/table-empty-row";
import { PaginationLimitOffset } from "@/shared/components/tables/pagination-limit-offset";
import { OrderBySelect } from "@/shared/components/tables/order-by-select";
import { DocumentStateBadge } from "@/shared/bsm/core/document/components/ui/document-state-badge";
import { ReferenceNumber } from "@/services/backend/documents/components/reference-number";

export function OrderTable({
  request,
  onRequestChange,
  renderRowLink,
  hidePagination = false,
  hideBorder = false,
}: {
  request: ListRequest;
  onRequestChange: (request: ListRequest) => void;
  renderRowLink: (orderId: string, children: ReactNode) => ReactNode;
  hidePagination?: boolean;
  hideBorder?: boolean;
}) {
  const { data: list, isLoading, error } = useHtzOrderListQuery(request);

  const emptyList = !isLoading && !error && list!.items.length === 0;

  return (
    <div className="grid gap-2">
      <TableCard className={hideBorder ? "border-none shadow-none" : ""}>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("Nummer")}</TableHead>
              <TableHead>{t("Status")}</TableHead>
              <TableHead className="whitespace-nowrap">
                {t("Eingang")}
              </TableHead>
              <TableHead className="whitespace-nowrap">
                {t("Zu Angebot")}
              </TableHead>
              <TableHead className="whitespace-nowrap">{t("Kanal")}</TableHead>
              <TableHead className="w-full whitespace-nowrap">
                {t("Kundenreferenz")}
              </TableHead>
              <TableHead className="whitespace-nowrap">
                {t("Aktualisiert am")}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && <TableLoadingRow colSpan={7} />}
            {error && <TableRtkErrorRow error={error} colSpan={7} />}
            {emptyList && (
              <TableEmptyRow
                colSpan={7}
                text={t("Keine Auftragseingänge gefunden")}
              />
            )}
            {list &&
              list.items.map((order) => (
                <OrderTableRow
                  key={order.id}
                  order={order}
                  renderRowLink={renderRowLink}
                />
              ))}
          </TableBody>
        </Table>
      </TableCard>
      {!hidePagination && list && !emptyList && (
        <PaginationLimitOffset
          offset={request.offset ?? 0}
          limit={request.limit ?? 0}
          totalCount={list.totalCount}
          setLimitOffset={(limit, offset) =>
            onRequestChange({ ...request, limit, offset })
          }
          className="ml-auto"
        />
      )}
    </div>
  );
}

function OrderTableRow({
  order,
  renderRowLink,
}: {
  order: Order;
  renderRowLink: (orderId: string, children: ReactNode) => ReactNode;
}) {
  return (
    <TableRow>
      <TableCell>
        {renderRowLink(
          order.id,
          <ReferenceNumber referenceNumber={order.referenceNumber} />,
        )}
      </TableCell>
      <TableCell>
        <DocumentStateBadge document={order} />
      </TableCell>
      <TableCell className="whitespace-nowrap">
        {formatDate(order.date ?? "")}
      </TableCell>
      <TableCell>
        {!order.offerId || isZeroUUID(order.offerId) ? (
          <span>{t("Ohne Angebot")}</span>
        ) : (
          <DocumentReferenceNumberById
            serviceKind={GSMServiceKind.HTZ}
            documentKind={GSMDocumentKind.Offer}
            documentId={order.offerId}
            showState
          />
        )}
      </TableCell>
      <TableCell className="whitespace-nowrap">
        {t(order.channel ?? "")}
      </TableCell>
      <TableCell>{t(order.customerReference)}</TableCell>
      <TableCell className="whitespace-nowrap">
        {formatDate(order.updatedAt)}
      </TableCell>
    </TableRow>
  );
}

export function OrderTableFilters({
  request,
  onRequestChange,
}: {
  request: ListRequest;
  onRequestChange: (request: ListRequest) => void;
}) {
  return (
    <div className="flex items-center gap-2">
      <OrderBySelect<string>
        orderBy={request.orderBy}
        onOrderByChange={(orderBy) => onRequestChange({ ...request, orderBy })}
        fields={[
          { label: t("Kanal"), value: "channel" },
          { label: t("Kundenreferenz"), value: "customerReference" },
          { label: t("Erstellt am"), value: "createdAt" },
          { label: t("Aktualisiert am"), value: "updatedAt" },
        ]}
      />
    </div>
  );
}

export function useDefaultHtzOrderListRequest(processId: string): ListRequest {
  return {
    process: { active: true, values: [processId] },
    customer: { active: false, values: [] },
    orderBy: [{ field: "createdAt" }],
    limit: 50,
    offset: 0,
  };
}
